import { useRef, Fragment } from 'react';
import { usePolarisVizContext, DEFAULT_CHART_PROPS, useTheme, uniqueId, InternalChartType, ChartState } from '@shopify/polaris-viz-core';
import { fillMissingDataPoints } from '../../utilities/fillMissingDataPoints.js';
import { getLineChartDataWithDefaults } from '../../utilities/getLineChartDataWithDefaults.js';
import { useThemeSeriesColors } from '../../hooks/useThemeSeriesColors.js';
import { Chart } from './Chart.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { getXAxisOptionsWithDefaults, getYAxisOptionsWithDefaults } from '../../utilities/getAxisOptions.js';
import { normalizeData } from '../../utilities/normalizeData.js';
import { SkipLink } from '../SkipLink/SkipLink.js';
import { ChartContainer } from '../ChartContainer/ChartContainer.js';
import { ChartSkeleton } from '../ChartSkeleton/ChartSkeleton.js';
import { useRenderTooltipContent } from '../../hooks/useRenderTooltipContent.js';

function LineChart(props) {
  const {
    defaultTheme
  } = usePolarisVizContext();
  const {
    annotations = [],
    data: dataSeries,
    emptyStateText,
    errorText,
    id,
    isAnimated,
    onError,
    renderLegendContent,
    renderHiddenLegendLabel,
    showLegend = true,
    skipLinkText,
    state,
    theme = defaultTheme,
    tooltipOptions,
    xAxisOptions,
    yAxisOptions
  } = { ...DEFAULT_CHART_PROPS,
    ...props
  };
  const data = fillMissingDataPoints(dataSeries, true);
  const selectedTheme = useTheme(theme);
  const seriesColors = useThemeSeriesColors(data, selectedTheme);
  const skipLinkAnchorId = useRef(uniqueId('lineChart'));
  const xAxisOptionsWithDefaults = getXAxisOptionsWithDefaults(xAxisOptions);
  const yAxisOptionsWithDefaults = getYAxisOptionsWithDefaults(yAxisOptions);
  const renderTooltip = useRenderTooltipContent({
    tooltipOptions,
    theme,
    data
  });
  const annotationsLookupTable = normalizeData(annotations, 'startKey');
  const dataWithDefaults = getLineChartDataWithDefaults(data, seriesColors);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [skipLinkText == null || skipLinkText.length === 0 || data.length === 0 ? null : /*#__PURE__*/jsx(SkipLink, {
      anchorId: skipLinkAnchorId.current,
      children: skipLinkText
    }), /*#__PURE__*/jsx(ChartContainer, {
      id: id,
      data: data,
      theme: theme,
      isAnimated: isAnimated,
      type: InternalChartType.Line,
      onError: onError,
      children: state !== ChartState.Success ? /*#__PURE__*/jsx(ChartSkeleton, {
        state: state,
        errorText: errorText,
        theme: theme
      }) : /*#__PURE__*/jsx(Chart, {
        annotationsLookupTable: annotationsLookupTable,
        data: dataWithDefaults,
        emptyStateText: emptyStateText,
        renderLegendContent: renderLegendContent,
        renderTooltipContent: renderTooltip,
        renderHiddenLegendLabel: renderHiddenLegendLabel,
        showLegend: showLegend,
        theme: theme,
        xAxisOptions: xAxisOptionsWithDefaults,
        yAxisOptions: yAxisOptionsWithDefaults,
        slots: props.slots
      })
    }), skipLinkText == null || skipLinkText.length === 0 ? null : /*#__PURE__*/jsx(SkipLink.Anchor, {
      id: skipLinkAnchorId.current
    })]
  });
}

export { LineChart };
